/**
 * @author        esraa.afifi@zumper.com (Esraa Afifi)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   MapBox Module Meta Reducer
 */

import { falseReducer, nullReducer, payloadReducer, trueReducer } from '@zcode/reducers'
import type { MainMapMetaTypes } from '@zcode/state-type'
import { combineReducers } from 'redux'
import { type ExtractActions, type Values, handleAction, handleActions } from 'redux-actions'
import type * as actions from '../actions'
import {
  DISABLE_SYNC,
  DISABLE_SYNC_ON_MOVE,
  ENABLE_SYNC,
  ENABLE_SYNC_ON_MOVE,
  FREEZE_ZOOM,
  HIDE,
  MOUNT,
  NOT_READY,
  READY,
  RESET_META,
  SHOW,
  SYNC,
  THAW_ZOOM,
  UNMOUNT,
  UNSYNC,
  UPDATE_BOX,
} from '../constants'

type Actions = ExtractActions<Values<typeof actions>>

const metaReducer = combineReducers<MainMapMetaTypes, Actions>({
  frozenZoom: handleActions<MainMapMetaTypes['frozenZoom'], Actions>(
    {
      [FREEZE_ZOOM]: payloadReducer,
      [THAW_ZOOM]: nullReducer,
    },
    null
  ),
  ready: handleActions(
    {
      [READY]: trueReducer,
      [RESET_META]: falseReducer,
      [NOT_READY]: falseReducer,
    },
    false
  ),
  mounted: handleActions(
    {
      [MOUNT]: trueReducer,
      [RESET_META]: falseReducer,
      [UNMOUNT]: falseReducer,
    },
    false
  ),
  syncOnMove: handleActions(
    {
      [ENABLE_SYNC_ON_MOVE]: trueReducer,
      [DISABLE_SYNC_ON_MOVE]: falseReducer,
    },
    true
  ),
  unsynced: handleActions(
    {
      [RESET_META]: falseReducer,
      [SYNC]: falseReducer,
      [UNSYNC]: trueReducer,
    },
    false
  ),
  syncDisabled: handleActions(
    {
      [ENABLE_SYNC]: falseReducer,
      [DISABLE_SYNC]: trueReducer,
    },
    false
  ),
  visible: handleActions(
    {
      [SHOW]: trueReducer,
      [HIDE]: falseReducer,
    },
    true
  ),
  box: handleAction<MainMapMetaTypes['box'], Actions, typeof UPDATE_BOX>(
    UPDATE_BOX,
    payloadReducer,
    null
  ),
})

export default metaReducer
